import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { CreateDashboardItemDTO, CreateGrafanaGraphDTO, CreateResponseDTO, DashboardGraph, FindItem, UpdateDashboardItemDTO } from "@dto/dashboard.dto";
import { GraphInterval } from "@dto/pageResponse.dto";
import AbstractDashboardSelect, { DashboardSelect, DashboardSelectProps } from "@parts/dashboard/select/AbstractDashboardSelect";
import { MenuDataNode } from "@parts/dashboard/select/DashboardContextMenu";
import { AxiosError } from "axios";
import { postApi, putApi } from "src/util/apiCalls";
import { API_URL } from "../../../data/Api";
import { Log } from "../../../service/log.service";

interface GraphDashboardSelectProps extends DashboardSelectProps {
  interval: GraphInterval;
}

export default class GraphDashboardSelect extends AbstractDashboardSelect<GraphDashboardSelectProps> {

  protected override showLegendCheckbox = true;

  protected updateItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: FindItem[], path: string) {
    const foundGraph = dashboardItems.find(fg => fg.dashboard_item_id === ds.id);
    if (!foundGraph) {
      const msg = 'Update graph in dashboard failed, ID lost: ' + ds.id;
      Log.error(msg);
      return;
    }
    foundGraph.dashboard_item_id = ds.id;
    foundGraph.interval = this.props.interval;
    foundGraph.dashboard_group = { dashboard_group_id: group.itemId, label: group.titleText };
    foundGraph.dashboard = { dashboard_id: dash.itemId, label: dash.titleText };
    foundGraph.path = path;
    foundGraph.legend = ds.legend;
  }

  private getPath(fullPath: string, dash: MenuDataNode, group: MenuDataNode) {
    return fullPath.substring(0, fullPath.lastIndexOf(dash.titleText + '/' + group.titleText) - 1).substring(this.DASHBOARD.length);
  }

  protected addItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: FindItem[], path: string) {
    dashboardItems.push({
      dashboard_item_id: ds.id || String(ds.selectId), interval: this.props.interval,
      dashboard_group: { dashboard_group_id: group.itemId, label: group.titleText },
      dashboard: { dashboard_id: dash.itemId, label: dash.titleText },
      path,
      legend: ds.legend
    });
  }

  protected showItemInDashboardWarn(dash: MenuDataNode, ds: DashboardSelect): boolean {
    const present = this.state.dashboardItems.some(fg => !this.isOwnItem(ds, fg) && fg.interval === this.props.interval && fg.dashboard.dashboard_id === dash.itemId);
    if (present) Log.warn('Graph is already present in this dashboard location.');
    return present;
  }

  protected postToGrafana(dashboard: MenuDataNode) {
    const c = this.props.common;
    return postApi<BasicResponseDTO, CreateGrafanaGraphDTO>(API_URL.DASHBOARD_GRAFANA + '/graph',
      {
        grafana_dashboard_uid: dashboard.itemId.replace(AbstractDashboardSelect.GRAFANA_PREFIX, ''),
        interval: this.props.interval,
        tab: c.tabName,
        subtab: c.subTabName,
        tabId: c.tabId,
        url: c.dashUrl
      }).catch((reason: AxiosError) => Log.error('Failed to add graph to Grafana dashboard ' + dashboard.titleText, reason));
  }

  protected postToDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode) {
    const c = this.props.common;
    return postApi<CreateResponseDTO, CreateDashboardItemDTO>(API_URL.DASHBOARD_ITEM,
      {
        dashboard_group_id: group.itemId,
        interval: this.props.interval,
        tab: c.tabName,
        subtab: c.subTabName,
        tabId: c.tabId,
        url: c.dashUrl,
        type: 'graph',
        options: { legend: ds.legend }
      }).then(response => {
        ds.id = response.data.data.id;
        this.addItem(ds, dash, group, this.props.common.dashboardItems, this.getPath(ds.value, dash, group));
      }, (reason: AxiosError) => Log.error('Failed to add graph to dashboard group ' + group.titleText, reason));
  }

  protected updateInDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode) {
    return putApi<BasicResponseDTO, UpdateDashboardItemDTO>(API_URL.DASHBOARD_ITEM + '/' + ds.id,
      {
        dashboard_group_id: group.itemId, x: DashboardGraph.DEFAULT_COORDINATE, y: DashboardGraph.DEFAULT_COORDINATE,
        options: { legend: ds.legend }
      }).then(() => {
        this.updateItem(ds, dash, group, this.props.common.dashboardItems, this.getPath(ds.value, dash, group));
      }, reason => Log.error('Failed to update graph in group ' + group.titleText, reason));
  }

  protected updateOptionsInDashboard(ds: DashboardSelect) {
    return putApi<BasicResponseDTO, UpdateDashboardItemDTO>(API_URL.DASHBOARD_ITEM + '/' + ds.id,
      { dashboard_group_id: ds.selected.itemId, options: { legend: ds.legend } }).then(() => {
        const found = this.props.common.dashboardItems?.find(fg => fg.dashboard_item_id === ds.id);
        if (found) found.legend = ds.legend;
      }, reason => Log.error('Failed to update graph legend in dashboard', reason));
  }

  protected addGrafana(ds: DashboardSelect, dash: MenuDataNode) {
    this.state.grafanaItems.push({
      dashboard: { grafana_dashboard_uid: dash.itemId, label: dash.titleText },
      grafana_item_uid: ds.id,
      interval: this.props.interval
    });
  }

}